import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Image, Spinner, Alert } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageLayout from "../../../layouts/PageLayout";
import axios from "axios";

export default function BlogFormPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isEditMode = Boolean(id);

    const passedBlog = location.state?.blog;

    const [loading, setLoading] = useState(isEditMode && !passedBlog);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [errors, setErrors] = useState({});

    const [formData, setFormData] = useState({
        name: passedBlog?.name || "",
        date: passedBlog?.date ? passedBlog.date.split("T")[0] : new Date().toISOString().split("T")[0],
        description: passedBlog?.description || "",
        meta_title: passedBlog?.meta_title || "",
        meta_description: passedBlog?.meta_description || "",
        meta_keywords: passedBlog?.meta_keywords || "",
        facebook_link: passedBlog?.facebook_link || "",
        instagram_link: passedBlog?.instagram_link || "",
        x_link: passedBlog?.x_link || "",
        linkedin_link: passedBlog?.linkedin_link || "",
        tiktok_link: passedBlog?.tiktok_link || "",
        pintrest_link: passedBlog?.pintrest_link || "",
    });

    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(
        passedBlog?.image ? `${process.env.REACT_APP_IMG_URL}/${passedBlog.image}` : null
    );

    const getAuthHeaders = () => ({
        Authorization: localStorage.getItem("authToken") ? `Bearer ${localStorage.getItem("authToken")}` : "",
    });

    // Fetch Blog data if editing directly without location state
    useEffect(() => {
        if (isEditMode && !passedBlog) {
            const fetchBlogDetails = async () => {
                try {
                    setLoading(true);
                    const response = await axios.get(`${process.env.REACT_APP_API_URL}/web-manager/blogs/${id}`, {
                        headers: getAuthHeaders(),
                    });
                    const data = response.data?.data || response.data;
                    setFormData({
                        name: data.name || "",
                        date: data.date ? data.date.split("T")[0] : "",
                        description: data.description || "",
                        meta_title: data.meta_title || "",
                        meta_description: data.meta_description || "",
                        meta_keywords: data.meta_keywords || "",
                        facebook_link: data.facebook_link || "",
                        instagram_link: data.instagram_link || "",
                        x_link: data.x_link || "",
                        linkedin_link: data.linkedin_link || "",
                        tiktok_link: data.tiktok_link || "",
                        pintrest_link: data.pintrest_link || "",
                    });
                    if (data.image) {
                        setImagePreview(`${process.env.REACT_APP_IMG_URL}/${data.image}`);
                    }
                } catch (err) {
                    setError("Failed to fetch blog details.");
                } finally {
                    setLoading(false);
                }
            };
            fetchBlogDetails();
        }
    }, [id, isEditMode, passedBlog]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
            setErrors((prev) => ({ ...prev, image: "" }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        setErrors({});

        try {
            const data = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    data.append(key, formData[key]);
                }
            });

            if (imageFile) {
                data.append("image", imageFile);
            }

            if (isEditMode) {
                data.append("_method", "PUT");
                await axios.post(`${process.env.REACT_APP_API_URL}/web-manager/blogs/${id}`, data, {
                    headers: getAuthHeaders(),
                });
            } else {
                await axios.post(`${process.env.REACT_APP_API_URL}/web-manager/blogs`, data, {
                    headers: getAuthHeaders(),
                });
            }

            navigate("/blogs");
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setError(err.response?.data?.message || "An error occurred while saving.");
            }
        } finally {
            setSaving(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ header: [1, 2, 3, 4, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["link", "image"],
            ["clean"],
        ],
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-2 text-muted">Loading details...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Container fluid className="py-4">
                <Row className="align-items-center mb-4">
                    <Col>
                        <h2 className="fw-bold mb-1">{isEditMode ? "Edit Blog Post" : "Create New Blog Post"}</h2>
                        <p className="text-muted mb-0">Fill in article details, rich text body.</p>
                    </Col>
                </Row>

                {error && <Alert variant="danger" dismissible onClose={() => setError("")}>{error}</Alert>}

                <Form onSubmit={handleSubmit}>
                    {/* Main Info */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 fw-bold border-0">Basic Details</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={8}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">
                                            Blog Title <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            isInvalid={!!errors.name}
                                            placeholder="Enter blog title"
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.name?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">
                                            Publish Date <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="date"
                                            value={formData.date}
                                            onChange={handleChange}
                                            isInvalid={!!errors.date}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.date?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                </Col>

                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">
                                            Cover Image
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            accept=".jpg,.jpeg,.png,.webp"
                                            onChange={handleImageChange}
                                            isInvalid={!!errors.image}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.image?.[0]}</Form.Control.Feedback>
                                    </Form.Group>
                                    {imagePreview && (
                                        <div className="mt-2">
                                            <Image src={imagePreview} rounded style={{ height: "120px", objectFit: "cover" }} />
                                        </div>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Description */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 fw-bold border-0">
                            Content Description <span className="text-danger">*</span>
                        </Card.Header>
                        <Card.Body>
                            <ReactQuill
                                theme="snow"
                                value={formData.description}
                                onChange={(content) => setFormData((prev) => ({ ...prev, description: content }))}
                                modules={quillModules}
                            />
                            {errors.description && <div className="text-danger small mt-1">{errors.description[0]}</div>}
                        </Card.Body>
                    </Card>

                    {/* SEO Metadata */}
                    {/* <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 fw-bold border-0">SEO Metadata</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">Meta Title</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="meta_title"
                                            value={formData.meta_title}
                                            onChange={handleChange}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">Meta Keywords</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="meta_keywords"
                                            value={formData.meta_keywords}
                                            onChange={handleChange}
                                            placeholder="e.g. news, tech, updates"
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">Meta Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            name="meta_description"
                                            value={formData.meta_description}
                                            onChange={handleChange}
                                            className="bg-light border-0 py-2 px-3"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card> */}

                    {/* Social Links */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Header className="bg-white py-3 fw-bold border-0">Social Sharing Links</Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">Facebook URL</Form.Label>
                                        <Form.Control type="url" name="facebook_link" value={formData.facebook_link} onChange={handleChange} className="bg-light border-0" />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">Instagram URL</Form.Label>
                                        <Form.Control type="url" name="instagram_link" value={formData.instagram_link} onChange={handleChange} className="bg-light border-0" />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">X (Twitter) URL</Form.Label>
                                        <Form.Control type="url" name="x_link" value={formData.x_link} onChange={handleChange} className="bg-light border-0" />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">LinkedIn URL</Form.Label>
                                        <Form.Control type="url" name="linkedin_link" value={formData.linkedin_link} onChange={handleChange} className="bg-light border-0" />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">TikTok URL</Form.Label>
                                        <Form.Control type="url" name="tiktok_link" value={formData.tiktok_link} onChange={handleChange} className="bg-light border-0" />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    <Form.Group className="mb-3">
                                        <Form.Label className="fw-semibold small text-muted text-uppercase">Pinterest URL</Form.Label>
                                        <Form.Control type="url" name="pintrest_link" value={formData.pintrest_link} onChange={handleChange} className="bg-light border-0" />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-2 mb-5">
                        <Button variant="secondary" className="px-4" onClick={() => navigate(-1)} disabled={saving}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" className="px-5" disabled={saving}>
                            {saving ? <Spinner animation="border" size="sm" /> : isEditMode ? "Update Blog" : "Create Blog"}
                        </Button>
                    </div>
                </Form>
            </Container>
        </PageLayout>
    );
}